// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/CaseDetail/components/ProviderMatchingProfileModal.tsx

import {
  createElement,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";

import {
  ApiError,
  getCommunityNavigatorProviderProfile,
  type NavigatorProviderIntakePreference,
  type NavigatorProviderProfile,
} from "../../../../../api/communityNavigator";
import { useNavigatorDialog } from "../../useNavigatorDialog";

type ProviderMatchingProfileModalProps = {
  familyDoctorId: number;
  onClose: () => void;
};

type DetailRowProps = {
  label: string;
  value: ReactNode;
};

type PreferenceRow = {
  label: string;
  value: string;
  isHardRule: boolean;
};

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const AGE_GROUP_LABELS: Record<string, string> = {
  InfantUnder1: "Newborn / infant under 1",
  Children: "Children",
  Adults: "Adults",
  Seniors65: "Seniors 65+",
  Seniors75: "Seniors 75+",
};

const AGE_PREF_LABELS: Record<string, string> = {
  Comfortable: "Comfortable",
  StrongFit: "Strong fit",
};

const COMPLEXITY_LABELS: Record<string, string> = {
  StandardOnly: "Standard complexity",
  Moderate: "Moderate complexity",
  ComfortableWithComplex: "Comfortable with complex patients",
  DiscussComplexCasesFirst: "Discuss complex cases with clinic first",
};

const CLINICAL_COMFORT_LABELS: Record<string, string> = {
  ChronicDiseaseDiabetes: "Chronic disease / diabetes",
  FrailSeniors: "Frail seniors",
  MentalHealth: "Mental health",
  NewbornCare: "Newborn care",
  WomensHealth: "Women’s health",
  PrenatalPostpartum: "Prenatal / postpartum",
  PalliativeCare: "Palliative care",
  DisabilityAccessibilityNeeds: "Disability / accessibility needs",
};

const GEOGRAPHY_LABELS: Record<string, string> = {
  NoPreference: "No geographic preference",
  SameFSAPreferred: "Same FSA preferred",
  SameCityPreferred: "Same city preferred",
  ReasonableDistancePreferred: "Within reasonable driving distance preferred",
};

function displayValue(value: ReactNode): ReactNode {
  if (value === null || value === undefined || value === "") return "Not provided";
  return value;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "Not provided";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatClock(value: string | null | undefined): string {
  if (!value) return "";
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return value;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

function formatYesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

function formatCapacity(value: number | null | undefined): string {
  if (value === null || value === undefined) return "Not provided";
  return String(value);
}

function toSafeExternalUrl(raw: string | null | undefined): string | null {
  const text = String(raw || "").trim();
  if (!text) return null;
  try {
    const withProtocol = /^https?:\/\//i.test(text) ? text : `https://${text}`;
    const url = new URL(withProtocol);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

function parseJsonValue(raw: string | null | undefined): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function humanizeIntakePreferences(
  preferences: NavigatorProviderIntakePreference[]
): PreferenceRow[] {
  const rows: PreferenceRow[] = [];

  for (const rule of preferences || []) {
    const key = String(rule.criterionKey || "").trim();
    const raw = String(rule.criterionValue || "").trim();
    if (!key) continue;

    if (key === "ageGroups") {
      const parsed = parseJsonValue(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        for (const [ageKey, pref] of Object.entries(
          parsed as Record<string, unknown>
        )) {
          const prefText = String(pref || "").trim();
          if (!prefText) continue;
          rows.push({
            label: AGE_GROUP_LABELS[ageKey] || ageKey,
            value: AGE_PREF_LABELS[prefText] || prefText,
            isHardRule: rule.isHardRule === true,
          });
        }
      }
      continue;
    }

    if (key === "complexityComfort") {
      if (!raw) continue;
      rows.push({
        label: "Complexity comfort",
        value: COMPLEXITY_LABELS[raw] || raw,
        isHardRule: rule.isHardRule === true,
      });
      continue;
    }

    if (key === "clinicalComfortAreas") {
      const parsed = parseJsonValue(raw);
      const values = Array.isArray(parsed)
        ? parsed.map((item) => String(item || "").trim()).filter(Boolean)
        : [];
      if (!values.length) continue;
      rows.push({
        label: "Clinical comfort areas",
        value: values
          .map((item) => CLINICAL_COMFORT_LABELS[item] || item)
          .join(", "),
        isHardRule: rule.isHardRule === true,
      });
      continue;
    }

    if (key === "geographyPreference") {
      if (!raw) continue;
      rows.push({
        label: "Geography preference",
        value: GEOGRAPHY_LABELS[raw] || raw,
        isHardRule: rule.isHardRule === true,
      });
      continue;
    }

    if (key === "navigatorNote") {
      if (!raw) continue;
      rows.push({
        label: "Navigator note",
        value: raw,
        isHardRule: rule.isHardRule === true,
      });
      continue;
    }
  }

  return rows;
}

function DetailRow({ label, value }: DetailRowProps) {
  return createElement(
    "div",
    { className: "cn-provider-profile-detail-row" },
    createElement("span", null, label),
    createElement("strong", null, displayValue(value))
  );
}

function ChipList({ items }: { items: string[] }) {
  if (!items.length) {
    return createElement("p", { className: "cn-provider-profile-empty" }, "Not provided");
  }

  return createElement(
    "ul",
    { className: "cn-provider-profile-chip-list" },
    items.map((item) =>
      createElement("li", { key: item }, item)
    )
  );
}

function ProfileSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return createElement(
    "section",
    { className: "cn-provider-profile-card" },
    createElement("h3", null, title),
    children
  );
}

export default function ProviderMatchingProfileModal({
  familyDoctorId,
  onClose,
}: ProviderMatchingProfileModalProps) {
  const dialogTitleId = `cn-provider-profile-title-${familyDoctorId}`;
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [profile, setProfile] = useState<NavigatorProviderProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useNavigatorDialog({
    open: true,
    onDismiss: onClose,
    dialogRef,
    initialFocusRef: closeButtonRef,
  });

  useEffect(() => {
    let cancelled = false;

    async function loadProfile() {
      setIsLoading(true);
      setLoadError(null);
      setProfile(null);

      try {
        const next = await getCommunityNavigatorProviderProfile(familyDoctorId);
        if (!cancelled) setProfile(next);
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err instanceof ApiError
              ? "Unable to load the provider profile right now."
              : "Unable to load the provider profile right now."
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void loadProfile();

    return () => {
      cancelled = true;
    };
  }, [familyDoctorId]);

  function handleBackdropMouseDown(event: ReactMouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }

  const doctor = profile?.doctor;
  const clinic = profile?.clinic;
  const bookingUrl = toSafeExternalUrl(doctor?.firstAppointmentBookingUrl);
  const websiteUrl = toSafeExternalUrl(clinic?.website);
  const preferenceRows = humanizeIntakePreferences(profile?.intakePreferences || []);
  const address = [clinic?.addressLine1, clinic?.addressLine2]
    .filter(Boolean)
    .join(", ");
  const cityLine = [clinic?.city, clinic?.province, clinic?.postalCode]
    .filter(Boolean)
    .join(", ");

  return createElement(
    "div",
    {
      className: "cn-provider-profile-modal-backdrop",
      role: "presentation",
      onMouseDown: handleBackdropMouseDown,
    },
    createElement(
      "aside",
      {
        className: "cn-provider-profile-modal",
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": dialogTitleId,
        tabIndex: -1,
        ref: dialogRef,
      },
      createElement(
        "div",
        { className: "cn-provider-profile-modal-header" },
        createElement(
          "div",
          null,
          createElement(
            "p",
            { className: "cn-case-detail-eyebrow" },
            "Read-only navigator view"
          ),
          createElement("h2", { id: dialogTitleId }, "Provider profile"),
          createElement(
            "p",
            null,
            "Review current doctor and clinic information before forwarding or coordinating patient follow-up."
          )
        ),
        createElement(
          "button",
          {
            type: "button",
            className: "cn-provider-profile-modal-close",
            onClick: onClose,
            "aria-label": "Close provider profile",
            ref: closeButtonRef,
          },
          "×"
        )
      ),

      createElement(
        "div",
        { className: "cn-provider-profile-modal-body" },
        isLoading
          ? createElement(
              "section",
              { className: "cn-provider-profile-card", "aria-live": "polite" },
              createElement("p", null, "Loading provider profile…")
            )
          : null,

        loadError
          ? createElement(
              "section",
              {
                className: "cn-provider-profile-card cn-provider-profile-card-muted",
                role: "alert",
              },
              createElement("p", null, loadError)
            )
          : null,

        !isLoading && !loadError && profile
          ? createElement(
              "div",
              { className: "cn-provider-profile-sections" },
              createElement(
                ProfileSection,
                { title: "Doctor" },
                createElement(
                  "div",
                  { className: "cn-provider-profile-detail-grid" },
                  createElement(DetailRow, {
                    label: "Doctor",
                    value: doctor?.fullName,
                  }),
                  createElement(DetailRow, {
                    label: "CPSO",
                    value: doctor?.cpso,
                  }),
                  createElement(DetailRow, {
                    label: "Gender",
                    value: doctor?.gender,
                  }),
                  createElement(DetailRow, {
                    label: "Accepting new patients",
                    value: formatYesNo(Boolean(doctor?.acceptingNewPatients)),
                  }),
                  createElement(DetailRow, {
                    label: "Accepting status updated",
                    value: formatDateTime(doctor?.acceptingStatusUpdatedAt),
                  }),
                  createElement(DetailRow, {
                    label: "Declared capacity",
                    value: formatCapacity(doctor?.declaredCapacity ?? null),
                  }),
                  createElement(DetailRow, {
                    label: "Remaining capacity",
                    value: formatCapacity(doctor?.remainingCapacity ?? null),
                  }),
                  createElement(DetailRow, {
                    label: "Capacity updated",
                    value: formatDateTime(doctor?.remainingCapacityUpdatedAt),
                  })
                )
              ),

              createElement(
                ProfileSection,
                { title: "Appointment / contact" },
                createElement(
                  "div",
                  { className: "cn-provider-profile-detail-grid" },
                  createElement(DetailRow, {
                    label: "First appointment booking",
                    value: bookingUrl
                      ? createElement(
                          "a",
                          {
                            href: bookingUrl,
                            target: "_blank",
                            rel: "noopener noreferrer",
                            className: "cn-provider-profile-external-link",
                          },
                          "Open first appointment booking"
                        )
                      : "Not provided",
                  }),
                  createElement(DetailRow, {
                    label: "Clinic phone",
                    value: clinic?.phone,
                  })
                )
              ),

              createElement(
                ProfileSection,
                { title: "Practice services" },
                createElement(
                  "div",
                  { className: "cn-provider-profile-service-grid" },
                  createElement(
                    "div",
                    null,
                    createElement("h4", null, "Languages"),
                    createElement(ChipList, {
                      items: profile.services.languages || [],
                    })
                  ),
                  createElement(
                    "div",
                    null,
                    createElement("h4", null, "Visit types"),
                    createElement(ChipList, {
                      items: profile.services.visitTypes || [],
                    })
                  ),
                  createElement(
                    "div",
                    null,
                    createElement("h4", null, "Coverage"),
                    createElement(ChipList, {
                      items: profile.services.coverages || [],
                    })
                  )
                )
              ),

              createElement(
                ProfileSection,
                { title: "Doctor intake preferences" },
                preferenceRows.length
                  ? createElement(
                      "ul",
                      { className: "cn-provider-profile-preference-list" },
                      preferenceRows.map((row) =>
                        createElement(
                          "li",
                          { key: `${row.label}-${row.value}` },
                          createElement("strong", null, row.label),
                          createElement(
                            "span",
                            null,
                            row.value,
                            row.isHardRule
                              ? createElement(
                                  "em",
                                  { className: "cn-provider-profile-hard-rule" },
                                  " Hard requirement"
                                )
                              : null
                          )
                        )
                      )
                    )
                  : createElement(
                      "p",
                      { className: "cn-provider-profile-empty" },
                      "Not provided"
                    )
              ),

              createElement(
                ProfileSection,
                { title: "Clinic information" },
                createElement(
                  "div",
                  { className: "cn-provider-profile-detail-grid" },
                  createElement(DetailRow, {
                    label: "Clinic",
                    value: clinic?.name,
                  }),
                  createElement(DetailRow, {
                    label: "Clinic setting",
                    value: clinic?.clinicSetting,
                  }),
                  createElement(DetailRow, {
                    label: "Address",
                    value: [address, cityLine].filter(Boolean).join(" · ") || null,
                  }),
                  createElement(DetailRow, {
                    label: "Phone",
                    value: clinic?.phone,
                  }),
                  createElement(DetailRow, {
                    label: "Fax",
                    value: clinic?.fax,
                  }),
                  createElement(DetailRow, {
                    label: "Website",
                    value: websiteUrl
                      ? createElement(
                          "a",
                          {
                            href: websiteUrl,
                            target: "_blank",
                            rel: "noopener noreferrer",
                            className: "cn-provider-profile-external-link",
                          },
                          clinic?.website || websiteUrl
                        )
                      : "Not provided",
                  }),
                  createElement(DetailRow, {
                    label: "Clinic administration email",
                    value: clinic?.administrationEmail,
                  })
                )
              ),

              createElement(
                ProfileSection,
                { title: "Clinic hours" },
                clinic?.operatingHours?.length
                  ? createElement(
                      "ul",
                      { className: "cn-provider-profile-hours-list" },
                      clinic.operatingHours.map((hour) =>
                        createElement(
                          "li",
                          { key: hour.dayOfWeek },
                          createElement(
                            "span",
                            null,
                            DAY_NAMES[hour.dayOfWeek] || `Day ${hour.dayOfWeek}`
                          ),
                          createElement(
                            "strong",
                            null,
                            hour.isClosed
                              ? "Closed"
                              : `${formatClock(hour.openTime)} – ${formatClock(
                                  hour.closeTime
                                )}`
                          )
                        )
                      )
                    )
                  : createElement(
                      "p",
                      { className: "cn-provider-profile-empty" },
                      "Not provided"
                    )
              ),

              createElement(
                ProfileSection,
                { title: "Accessibility" },
                createElement(ChipList, {
                  items: clinic?.accessibilityFeatures || [],
                })
              ),

              createElement(
                ProfileSection,
                { title: "On-site services" },
                createElement(ChipList, {
                  items: clinic?.onSiteServices || [],
                })
              ),

              doctor?.intakeNotes
                ? createElement(
                    ProfileSection,
                    { title: "Doctor notes for Navigators" },
                    createElement(
                      "p",
                      { className: "cn-provider-profile-notes" },
                      doctor.intakeNotes
                    )
                  )
                : null,

              createElement(
                "section",
                {
                  className:
                    "cn-provider-profile-card cn-provider-profile-card-muted",
                },
                createElement("h3", null, "Navigator access boundary"),
                createElement(
                  "p",
                  null,
                  "This view is read-only. It shows the provider’s current operational information and does not allow editing doctor or clinic data."
                )
              )
            )
          : null
      )
    )
  );
}
